import type { ReactNode } from 'react';
import { Alert } from '../../core/atoms/Alert';
import { Button } from '../../core/atoms/Button';
import { Group } from '../../core/atoms/Group';
import { Loader } from '../../core/atoms/Loader';
import { Stack } from '../../core/atoms/Stack';
import { PageHeader } from '../../core/molecules/PageHeader';

export interface EntityDetailShellProps {
  isLoading: boolean;
  /** True when the record failed to load or does not exist. */
  notFound: boolean;
  /** Page title once loaded — usually the record name. */
  title: string;
  description?: string;
  notFoundTitle: string;
  /** Label + handler for the "back to list" button shown in both states. */
  backLabel: string;
  onBack: () => void;
  /** Extra header buttons rendered next to Back (e.g. Edit). */
  actions?: ReactNode;
  children: ReactNode;
}

/** Loading / not-found / header scaffold shared by the tenant detail pages. */
export function EntityDetailShell({
  isLoading,
  notFound,
  title,
  description,
  notFoundTitle,
  backLabel,
  onBack,
  actions,
  children,
}: EntityDetailShellProps) {
  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (notFound) {
    return (
      <Alert color="red" title={notFoundTitle}>
        <Button variant="light" mt="sm" onClick={onBack}>
          {backLabel}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={title}
        description={description}
        actions={
          <Group>
            <Button variant="default" onClick={onBack}>
              Back
            </Button>
            {actions}
          </Group>
        }
      />
      {children}
    </Stack>
  );
}
